import { useEffect } from 'react';
import { Row, Col } from 'antd';
import styled from 'styled-components';
import router, { useRouter } from 'next/router';
import { ThirdPartyLinksTypeQuery, ThirdPartyLinksType } from 'constant/constants';
import Header from 'components/Header';
import PageContainer from 'components/PageContainer';
import MetaHeader from 'components/MetaHeader';
import * as fbq from 'lib/fpixel';
import { getCategoryConfig, reorderCategories } from 'utils/categories';
import PlatformCard from 'components/PlatformCard';
import camelCase from 'lodash/camelCase';
import { getMicrositeServiceType } from 'services';
import { delQuery } from 'utils/common';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';
import PoweredByMomos from 'components/PoweredByMomos';
import { Brand, MicrositeCategoriesConfigType, MicrositeConfig, ThirdPartyLink } from 'utils/types';
import { GetServerSideProps } from 'next';

interface OrderForDeliveryProps {
  brandDetails: Brand;
  micrositeConfig: MicrositeConfig;
  data: ThirdPartyLink[];
}

export default function OrderForDelivery({ data, brandDetails, micrositeConfig }: OrderForDeliveryProps) {
  let categoriesData = [...data];

  const { asPath } = useRouter();

  const { micrositeCategoriesConfig } = micrositeConfig || {};

  const micrositeCategoriesConfigData =
    micrositeCategoriesConfig?.[camelCase(ThirdPartyLinksType.FOOD_DELIVERY) as keyof MicrositeCategoriesConfigType];

  if (micrositeCategoriesConfigData) {
    const deliveryPlatformsIds = micrositeCategoriesConfigData?.map((o) => o.categoryId);

    categoriesData = reorderCategories(categoriesData, deliveryPlatformsIds, 'categoryId');
  }

  useEffect(() => {
    if (categoriesData?.length === 1) {
      fbq.event('Order for Delivery on', { content_name: categoriesData[0]?.category });
      ga.event({ ...TRACKING.ORDER_FOR_DELIVERY.PLATFORM, value: categoriesData[0]?.category });
      router.push(`${delQuery(asPath)}/${categoriesData[0]?.category}`);
    }
  }, [categoriesData]);

  return (
    <PageWrap>
      <MetaHeader
        title="Order for Delivery"
        image={brandDetails?.logoUrl ? brandDetails.logoUrl : undefined}
        pixelId={micrositeConfig?.micrositePixelId}
      />
      <Header
        brandDetails={brandDetails}
        theme={micrositeConfig?.micrositeTheme}
        hideMenuLink={!micrositeConfig?.menuFlag}
      />
      <PageContainer>
        <div className="mb-20">{'Order'}</div>
        <h2 className="heading-1 mb-30">{'Select A Platform'}</h2>
        <Row gutter={[16, 16]} className="options-wrap">
          {categoriesData.map((category, key) => {
            const categoryConfig: any = getCategoryConfig(
              micrositeConfig?.micrositeCategoriesConfig,
              category.categoryId,
              camelCase(ThirdPartyLinksType.FOOD_DELIVERY),
            );

            const promoMessage = categoryConfig?.promotionalMessage;

            const isYourWebsiteCategory = category.category === 'Your Website';
            const showBrandLogo = isYourWebsiteCategory ? categoryConfig?.brandLogoFlag : false;

            const platformLogo = showBrandLogo ? brandDetails.logoUrl : category.logo;

            return (
              <Col lg={8} key={key} sm={12} xs={12}>
                <PlatformCard
                  platformName={category.category}
                  onClick={() => {
                    fbq.event('Order for Delivery on', { content_name: category.category });
                    ga.event({ ...TRACKING.ORDER_FOR_DELIVERY.PLATFORM, value: category.category });

                    router.push(`${delQuery(asPath)}/${category.category}`);
                  }}
                  promoMessage={promoMessage}
                  platformLogo={platformLogo}
                  isBrandLogo={showBrandLogo}
                  brandLogoBg={categoryConfig?.brandLogoBackground}
                  isYourWebsiteCategory={isYourWebsiteCategory}
                  theme={{ highlightColor: micrositeConfig?.micrositeTheme?.highlightColor }}
                />
              </Col>
            );
          })}
        </Row>
        <PoweredByMomos />
      </PageContainer>
    </PageWrap>
  );
}

export const getServerSideProps: GetServerSideProps = async ({ params, req }) => {
  const {
    categories = [],
    brandDetails = null,
    micrositeConfig = null,
  } = await getMicrositeServiceType(req, params?.slug, ThirdPartyLinksTypeQuery.FOOD_DELIVERY);

  let filteredCategories: ThirdPartyLink[] = categories;

  if (micrositeConfig?.micrositeBlacklistComponents) {
    filteredCategories = filteredCategories.filter(
      (o) => !micrositeConfig.micrositeBlacklistComponents.includes(o.categoryId),
    );
  }

  return {
    props: {
      data: filteredCategories,
      slug: params?.slug,
      brandDetails,
      micrositeConfig,
    },
  };
};

const PageWrap = styled.div`
  .options-wrap {
    margin-bottom: 30px;
    margin-top: 30px;
  }
`;
